import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, PictureOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import CostSummary from '../components/CostSummary';
import LoadingCard from '../components/LoadingCard';
import type { CreditBalance } from '../types';
import type {
  CharacterDescriptionRefreshState,
  MarketingActionSignal,
  MarketingCharacterAsset,
  MarketingCharacterGenerationRecord,
  MarketingCharacterPresetId,
  MarketingCharacterRecord,
  MarketingCharacterSizeOption,
  MarketingCharacterStudioView,
  MarketingCharacterVisualStyleId,
  MarketingInspirationItem,
} from './types';
import { buildMarketingInspirationContext, sortMarketingInspirationItems } from './inspiration';
import MarketingInspirationPicker from './MarketingInspirationPicker';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

interface MarketingCharacterBookContext {
  title?: string;
  author?: string;
  series?: string;
  seriesNumber?: string;
  genres?: string[];
  coverReferenceUrl?: string | null;
  hasCoverReference?: boolean;
}

interface MarketingCharacterBootstrapResponse {
  book?: MarketingCharacterBookContext;
  characters?: MarketingCharacterRecord[];
  sizeOptionsByAspectRatio?: Record<string, Record<string, string>>;
}

interface ImageModelOption {
  id: string;
  displayName: string;
  apiModel: string;
  provider: 'openai' | 'openrouter_image' | string;
  enabledModes?: string[];
}

interface MarketingCharacterModuleProps {
  apiBase: string;
  bookId: string;
  userId: string;
  bookTitle?: string;
  actionSignal?: MarketingActionSignal | null;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  initialStudioView?: MarketingCharacterStudioView;
  allowedStudioViews?: MarketingCharacterStudioView[];
  libraryTitle?: string;
  inspirationRefreshSignal?: number;
}

interface CharacterGalleryGenerationEntry {
  kind: 'generation';
  key: string;
  id: string;
  createdAt?: string;
  characterId?: string;
  characterName?: string;
  stylePreset?: MarketingCharacterPresetId | string;
  visualStyle?: MarketingCharacterVisualStyleId | string;
  customVisualStyle?: string;
  size?: string;
  status?: string;
  prompt?: string;
  requested?: number;
  generated?: number;
  failedCount?: number;
  estimatedCredits?: number;
  chargedCredits?: number;
  error?: string;
  inspirationSources?: MarketingCharacterGenerationRecord['inspirationSources'];
}

interface CharacterGalleryAssetEntry {
  kind: 'asset';
  key: string;
  id: string;
  createdAt?: string;
  asset: MarketingCharacterAsset;
}

type CharacterGalleryEntry = CharacterGalleryGenerationEntry | CharacterGalleryAssetEntry;
type CharacterAuthoringMode = 'guided' | 'customize';
const CHARACTER_STUDIO_AUTHORING_MODE_KEY = 'marketing_character_authoring_mode';

const DEFAULT_ALLOWED_VIEWS: MarketingCharacterStudioView[] = ['create', 'library'];
const DEFAULT_VISUAL_STYLE: MarketingCharacterVisualStyleId = 'realistic';
const MAX_CHARACTER_REFERENCE_ASSETS = 4;
const VISUAL_STYLE_OPTIONS: Array<{
  value: MarketingCharacterVisualStyleId;
  label: string;
  promptInstruction: string;
}> = [
  {
    value: 'realistic',
    label: 'Realistic',
    promptInstruction:
      'Visual style mandate: photorealistic character rendering with believable skin, lighting, materials, and cinematic realism. Do not drift into animation or illustration.',
  },
  {
    value: 'three_d_animated',
    label: '3D Animated',
    promptInstruction:
      'Visual style mandate: polished 3D animated feature-film rendering with dimensional stylization, expressive forms, and premium cinematic animation appeal. Do not make this look live action.',
  },
  {
    value: 'hand_drawn',
    label: 'Hand Drawn',
    promptInstruction:
      'Visual style mandate: hand-drawn illustrated rendering with visible line or painterly mark-making and unmistakably illustrated surfaces. Do not make this look photoreal or CGI.',
  },
  {
    value: 'anime',
    label: 'Anime',
    promptInstruction:
      'Visual style mandate: premium anime-style rendering with expressive eyes, stylized hair, clean line treatment, and high-end key-art appeal. Do not drift into western comic or photoreal rendering.',
  },
  {
    value: 'graphic_novel',
    label: 'Graphic Novel',
    promptInstruction:
      'Visual style mandate: bold graphic-novel rendering with strong shape language, graphic contrast, and stylized illustrative energy. Avoid soft realism and generic animation looks.',
  },
  {
    value: 'storybook_illustration',
    label: 'Storybook Illustration',
    promptInstruction:
      'Visual style mandate: lush storybook illustration with painterly detail, curated color harmony, elegant silhouettes, and premium cover-art sensibility. Avoid photoreal or hard-edged CGI treatment.',
  },
  {
    value: 'custom',
    label: 'Custom',
    promptInstruction:
      'Visual style mandate: follow the user-provided custom visual style direction exactly and keep it consistent across the generated portrait.',
  },
];
const STYLE_PRESET_OPTIONS: Array<{ value: MarketingCharacterPresetId; label: string }> = [
  { value: 'cinematic_portrait', label: 'Cinematic Portrait' },
  { value: 'illustrated_concept', label: 'Illustrated Concept' },
  { value: 'social_avatar', label: 'Social Avatar' },
  { value: 'character_poster', label: 'Character Poster' },
  { value: 'romance_glamour', label: 'Romance Glamour' },
  { value: 'dark_fantasy', label: 'Dark Fantasy' },
  { value: 'mystery_noir', label: 'Mystery Noir' },
  { value: 'action_still', label: 'Action Still' },
  { value: 'cozy_editorial', label: 'Cozy Editorial' },
  { value: 'young_adult_cover', label: 'Young Adult Cover' },
];
const STYLE_PRESET_LABELS: Record<MarketingCharacterPresetId, string> = {
  cinematic_portrait: 'Cinematic Portrait',
  illustrated_concept: 'Illustrated Concept',
  social_avatar: 'Social Avatar',
  character_poster: 'Character Poster',
  romance_glamour: 'Romance Glamour',
  dark_fantasy: 'Dark Fantasy',
  mystery_noir: 'Mystery Noir',
  action_still: 'Action Still',
  cozy_editorial: 'Cozy Editorial',
  young_adult_cover: 'Young Adult Cover',
};
const STYLE_PRESET_HINTS: Record<MarketingCharacterPresetId, string> = {
  cinematic_portrait: 'Dramatic lighting, realistic detail, premium poster energy.',
  illustrated_concept: 'Polished concept-art framing with stylized publishing appeal.',
  social_avatar: 'Bold, readable thumbnail framing for avatars and promos.',
  character_poster: 'Hero-driven one-sheet energy with high-impact publishing composition.',
  romance_glamour: 'Soft-luxury beauty lighting and emotionally charged romantic polish.',
  dark_fantasy: 'Moody magic, gothic atmosphere, and high-drama fantasy styling.',
  mystery_noir: 'Shadow-heavy suspense framing with noir contrast and investigative tension.',
  action_still: 'Dynamic mid-movement framing with cinematic urgency and physical stakes.',
  cozy_editorial: 'Warm, approachable bookstore-friendly styling with inviting lifestyle polish.',
  young_adult_cover: 'Trendy YA cover energy with expressive styling and strong shelf appeal.',
};
const ASPECT_RATIO_OPTIONS = [
  { value: '2:3', label: '2:3 (Portrait)' },
  { value: '1:1', label: '1:1 (Square)' },
];
const CHARACTER_IMAGE_UNIT_COST = 1000;
const DEFAULT_CHARACTER_IMAGE_MODELS: ImageModelOption[] = [
  {
    id: 'gpt-image-2',
    displayName: 'OpenAI Image 2',
    apiModel: 'gpt-image-2',
    provider: 'openai',
    enabledModes: ['marketing_character'],
  },
];
const DEFAULT_SIZE_OPTIONS_BY_ASPECT: Record<string, Record<string, string>> = {
  '2:3': {
    '768x1152': 'Small',
    '1024x1536': 'Medium',
    '1280x1920': 'Large',
  },
  '1:1': {
    '1024x1024': 'Medium',
    '1536x1536': 'Large',
    '1920x1920': 'XL',
  },
};

const getAvailableCredits = (balance?: CreditBalance | null): number => {
  if (!balance) return 0;
  return Number(balance.available_balance ?? balance.credit_balance ?? 0);
};

const normalizeDescriptionRefreshState = (
  refresh?: CharacterDescriptionRefreshState | null
): CharacterDescriptionRefreshState => {
  const status = String(refresh?.status || 'idle').trim().toLowerCase() || 'idle';
  const usedCount = Math.max(0, Number(refresh?.usedCount || 0));
  const remainingCount =
    refresh?.remainingCount == null ? (usedCount >= 1 ? 0 : 1) : Math.max(0, Number(refresh.remainingCount || 0));
  return {
    status,
    usedCount,
    remainingCount,
    requestedAt: refresh?.requestedAt || null,
    startedAt: refresh?.startedAt || null,
    completedAt: refresh?.completedAt || null,
    lastError: refresh?.lastError || null,
    chapterCount: Number(refresh?.chapterCount || 0),
    manifestId: refresh?.manifestId || null,
  };
};

const isDescriptionRefreshActive = (refresh?: CharacterDescriptionRefreshState | null) => {
  const status = String(refresh?.status || '').trim().toLowerCase();
  return status === 'queued' || status === 'running';
};

const getDescriptionRefreshStatusLabel = (refresh?: CharacterDescriptionRefreshState | null) => {
  const normalized = normalizeDescriptionRefreshState(refresh);
  if (normalized.status === 'queued') return 'Waiting for an analysis worker...';
  if (normalized.status === 'running') {
    return normalized.chapterCount
      ? `Analyzing ${normalized.chapterCount} chapter${normalized.chapterCount === 1 ? '' : 's'}...`
      : 'Analyzing character description...';
  }
  if (normalized.status === 'failed') return 'Last refresh failed';
  if ((normalized.usedCount || 0) >= 1 || (normalized.remainingCount || 0) <= 0) {
    return '1 free refresh used';
  }
  return '';
};

const buildCharacterPrompt = (
  book: MarketingCharacterBookContext | null,
  character: MarketingCharacterRecord | null,
  stylePreset: MarketingCharacterPresetId,
  visualStyle: MarketingCharacterVisualStyleId,
  customVisualStyle?: string
): string => {
  if (!character) {
    return '';
  }
  const presetInstruction: Record<MarketingCharacterPresetId, string> = {
    cinematic_portrait:
      'Create a cinematic portrait with dramatic lighting, premium realism, and poster-grade polish.',
    illustrated_concept:
      'Create polished illustrated concept art with clear silhouette design and premium book-market appeal.',
    social_avatar:
      'Create a bold social-ready character portrait with clean framing and strong thumbnail readability.',
    character_poster:
      'Create a premium character-poster image with bold composition, strong subject clarity, and cinematic one-sheet impact.',
    romance_glamour:
      'Create a glamorous romantic portrait with soft luxury lighting, emotional intimacy, and polished commercial appeal.',
    dark_fantasy:
      'Create a dark fantasy character portrait with moody atmosphere, arcane detail, and elevated gothic styling.',
    mystery_noir:
      'Create a noir-inspired mystery portrait with dramatic shadows, crisp contrast, and suspenseful detective energy.',
    action_still:
      'Create a high-intensity action still with dynamic pose, cinematic motion cues, and premium blockbuster polish.',
    cozy_editorial:
      'Create a warm editorial-style portrait with inviting lifestyle polish, natural charm, and approachable bookstore appeal.',
    young_adult_cover:
      'Create a modern young-adult cover-style portrait with expressive styling, trend-aware polish, and strong shelf presence.',
  };
  const bookBits = [
    book?.title ? `Book: ${book.title}.` : '',
    book?.author ? `Author: ${book.author}.` : '',
    book?.series
      ? `Series: ${book.series}${book?.seriesNumber ? `, book ${book.seriesNumber}` : ''}.`
      : '',
    book?.genres?.length ? `Genres: ${book.genres.slice(0, 4).join(', ')}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const aliasText = character.aliases?.length
    ? `Aliases: ${character.aliases.slice(0, 4).join(', ')}.`
    : '';
  const summaryText = character.descriptionSummary
    ? `Visual description cues: ${character.descriptionSummary}.`
    : '';
  const genderText = character.gender ? `Gender presentation in source data: ${character.gender}.` : '';
  return [
    presetInstruction[stylePreset],
    visualStyle === 'custom'
      ? `Visual style mandate: ${String(customVisualStyle || '').trim() || 'follow the user-provided custom visual style direction exactly'}. Keep this custom visual style unmistakable in the final image.`
      : VISUAL_STYLE_OPTIONS.find(option => option.value === visualStyle)?.promptInstruction ||
        VISUAL_STYLE_OPTIONS[0].promptInstruction,
    `Create ${character.canonicalName} from the user's book.`,
    bookBits,
    summaryText,
    genderText,
    aliasText,
    'Keep the character faithful to the source material, focus on one clear subject, and avoid text, watermarks, or collage layouts.',
  ]
    .filter(Boolean)
    .join(' ');
};

const getCharacterVisualStyleLabel = (visualStyle: MarketingCharacterVisualStyleId, customVisualStyle?: string): string => {
  if (visualStyle === 'custom') {
    const custom = String(customVisualStyle || '').trim();
    return custom ? `Custom: ${custom}` : 'Custom';
  }
  return VISUAL_STYLE_OPTIONS.find(option => option.value === visualStyle)?.label || visualStyle;
};

const formatTimestamp = (value?: string): string => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const getErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return String(data?.detail || data?.message || fallback);
  } catch {
    try {
      const text = await response.text();
      return text || fallback;
    } catch {
      return fallback;
    }
  }
};

const getAssetPreviewCandidates = (asset?: MarketingCharacterAsset | null): string[] => {
  if (!asset) return [];
  return [...new Set([
    asset.previewStreamUrl,
    asset.imageStreamUrl,
    asset.previewUrl,
    asset.imageUrl,
  ].map(value => String(value || '').trim()).filter(Boolean))];
};

const resolveAssetImageSrc = (asset?: MarketingCharacterAsset | null): string => {
  if (!asset) return '';
  return asset.imageStreamUrl || asset.imageUrl || asset.previewStreamUrl || asset.previewUrl || '';
};

const CharacterPortraitThumbnail: React.FC<{
  asset: MarketingCharacterAsset;
  alt: string;
  style?: React.CSSProperties;
}> = ({ asset, alt, style }) => {
  const candidates = useMemo(() => getAssetPreviewCandidates(asset), [asset]);
  const candidatesKey = candidates.join('|');
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [asset.id, candidatesKey]);

  const source = candidates[candidateIndex];
  if (!source) {
    return (
      <div
        role="img"
        aria-label={`${alt} preview unavailable`}
        style={{
          display: 'grid',
          placeItems: 'center',
          gap: 4,
          minHeight: 72,
          padding: 8,
          background: 'var(--surface-muted)',
          color: 'var(--text-secondary)',
          fontSize: 11,
          textAlign: 'center',
          ...style,
        }}
      >
        <PictureOutlined />
        <span>Preview unavailable</span>
      </div>
    );
  }

  return (
    <img
      src={source}
      alt={alt}
      onError={() => setCandidateIndex(index => index + 1)}
      style={style}
    />
  );
};

const mapSizeOptions = (
  sizeOptionsByAspectRatio: Record<string, Record<string, string>>,
  aspect: '2:3' | '1:1'
): MarketingCharacterSizeOption[] =>
  Object.entries(sizeOptionsByAspectRatio[aspect] || {}).map(([value, label]) => ({
    value,
    label: `${label} (${value})`,
  }));

const resolveDefaultSize = (
  sizeOptionsByAspectRatio: Record<string, Record<string, string>>,
  aspect: '2:3' | '1:1'
): string => {
  const preferredDefault = aspect === '1:1' ? '1024x1024' : '1024x1536';
  const options = sizeOptionsByAspectRatio[aspect] || {};
  if (options[preferredDefault]) {
    return preferredDefault;
  }
  return Object.keys(options)[0] || preferredDefault;
};

const isGenerationInFlight = (generation?: MarketingCharacterGenerationRecord | null): boolean => {
  const normalized = String(generation?.status || '').toLowerCase();
  return normalized === 'queued' || normalized === 'pending' || normalized === 'running' || normalized === 'retrying';
};

const characterGenerationStatusColor = (status?: string): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'failed' || normalized === 'error') return 'error';
  if (normalized === 'queued' || normalized === 'pending' || normalized === 'retrying') return 'gold';
  if (normalized === 'running') return 'processing';
  return 'default';
};

const MarketingCharacterModule: React.FC<MarketingCharacterModuleProps> = ({
  apiBase,
  bookId,
  userId,
  bookTitle,
  actionSignal,
  creditBalance,
  fetchCreditBalance,
  initialStudioView = 'create',
  allowedStudioViews = DEFAULT_ALLOWED_VIEWS,
  libraryTitle = 'Character Image Library',
  inspirationRefreshSignal,
}) => {
  const allowedStudioViewsKey = useMemo(
    () => (allowedStudioViews.length ? [...allowedStudioViews].join('|') : DEFAULT_ALLOWED_VIEWS.join('|')),
    [allowedStudioViews]
  );
  const stableAllowedStudioViews = useMemo<MarketingCharacterStudioView[]>(
    () => (allowedStudioViews.length ? allowedStudioViews : DEFAULT_ALLOWED_VIEWS),
    [allowedStudioViewsKey]
  );
  const availableViewSet = useMemo(
    () => new Set<MarketingCharacterStudioView>(stableAllowedStudioViews),
    [stableAllowedStudioViews]
  );
  const defaultView = availableViewSet.has(initialStudioView)
    ? initialStudioView
    : stableAllowedStudioViews[0] || 'create';
  const [studioView, setStudioView] = useState<MarketingCharacterStudioView>(defaultView);
  const [authoringMode, setAuthoringMode] = useState<CharacterAuthoringMode>(() => {
    if (typeof window === 'undefined') return 'guided';
    return window.localStorage.getItem(CHARACTER_STUDIO_AUTHORING_MODE_KEY) === 'customize'
      ? 'customize'
      : 'guided';
  });
  const [bookContext, setBookContext] = useState<MarketingCharacterBookContext | null>(null);
  const [characters, setCharacters] = useState<MarketingCharacterRecord[]>([]);
  const [assets, setAssets] = useState<MarketingCharacterAsset[]>([]);
  const [inspirationItems, setInspirationItems] = useState<MarketingInspirationItem[]>([]);
  const [inspirationLoading, setInspirationLoading] = useState<boolean>(false);
  const [selectedInspirationIds, setSelectedInspirationIds] = useState<string[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('');
  const [stylePreset, setStylePreset] = useState<MarketingCharacterPresetId>('cinematic_portrait');
  const [visualStyle, setVisualStyle] = useState<MarketingCharacterVisualStyleId>(DEFAULT_VISUAL_STYLE);
  const [customVisualStyle, setCustomVisualStyle] = useState<string>('');
  const [aspectRatio, setAspectRatio] = useState<'2:3' | '1:1'>('2:3');
  const [sizeOptionsByAspectRatio, setSizeOptionsByAspectRatio] = useState<
    Record<string, Record<string, string>>
  >(DEFAULT_SIZE_OPTIONS_BY_ASPECT);
  const [imageSize, setImageSize] = useState<string>(resolveDefaultSize(DEFAULT_SIZE_OPTIONS_BY_ASPECT, '2:3'));
  const [imageCount, setImageCount] = useState<number>(1);
  const [imageModelOptions, setImageModelOptions] = useState<ImageModelOption[]>(DEFAULT_CHARACTER_IMAGE_MODELS);
  const [selectedImageModel, setSelectedImageModel] = useState<string>('gpt-image-2');
  const [imageModelOptionsLoading, setImageModelOptionsLoading] = useState<boolean>(false);
  const [useCoverAsStyleReference, setUseCoverAsStyleReference] = useState<boolean>(false);
  const [selectedReferenceAssetIds, setSelectedReferenceAssetIds] = useState<string[]>([]);
  const [referencePickerOpen, setReferencePickerOpen] = useState<boolean>(false);
  const [referenceAssetSearch, setReferenceAssetSearch] = useState<string>('');
  const [guidanceMode, setGuidanceMode] = useState<'inspiration' | 'portraits'>('inspiration');
  const [prompt, setPrompt] = useState<string>('');
  const [promptDirty, setPromptDirty] = useState<boolean>(false);
  const [bootstrapLoading, setBootstrapLoading] = useState<boolean>(false);
  const [descriptionRefreshSubmittingId, setDescriptionRefreshSubmittingId] = useState<string>('');
  const [assetsLoading, setAssetsLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [bootstrapError, setBootstrapError] = useState<string>('');
  const [recentGenerations, setRecentGenerations] = useState<MarketingCharacterGenerationRecord[]>([]);
  const [activeGeneration, setActiveGeneration] = useState<MarketingCharacterGenerationRecord | null>(null);
  const [previewAsset, setPreviewAsset] = useState<MarketingCharacterAsset | null>(null);
  const [previewGeneration, setPreviewGeneration] = useState<MarketingCharacterGenerationRecord | null>(null);
  const [previewAssetLoading, setPreviewAssetLoading] = useState<boolean>(false);
  const [selectedLibraryAssetId, setSelectedLibraryAssetId] = useState<string>('');
  const [selectedLibraryGenerationId, setSelectedLibraryGenerationId] = useState<string>('');
  const [assetLibrarySearch, setAssetLibrarySearch] = useState<string>('');
  const [assetLibraryCharacterFilter, setAssetLibraryCharacterFilter] = useState<string>('all');
  const [assetLibraryPresetFilter, setAssetLibraryPresetFilter] = useState<string>('all');
  const [costBreakdownOpen, setCostBreakdownOpen] = useState<boolean>(false);
  const generationNoticeRef = useRef<string>('');
  const lastInspirationRefreshSignalRef = useRef(inspirationRefreshSignal);
  const availableCredits = useMemo(() => getAvailableCredits(creditBalance), [creditBalance]);
  const estimatedCredits = imageCount * CHARACTER_IMAGE_UNIT_COST;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CHARACTER_STUDIO_AUTHORING_MODE_KEY, authoringMode);
  }, [authoringMode]);
  const selectedImageModelConfig = useMemo(
    () => imageModelOptions.find(model => model.id === selectedImageModel || model.apiModel === selectedImageModel) || DEFAULT_CHARACTER_IMAGE_MODELS[0],
    [imageModelOptions, selectedImageModel]
  );
  const createSectionStyle: React.CSSProperties = {
    background: 'var(--surface-muted)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 10,
    padding: 10,
  };
  const overlayCardStyle: React.CSSProperties = {
    background: 'rgba(15, 23, 42, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    color: '#fff',
  };
  const selectedCharacter = useMemo(
    () => characters.find(item => item.id === selectedCharacterId) || null,
    [characters, selectedCharacterId]
  );
  const characterPortraitCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const characterIdByName = new Map(
      characters.map(character => [String(character.canonicalName || '').trim().toLowerCase(), character.id])
    );
    assets.forEach(asset => {
      const assetCharacterId = String(asset.characterId || '').trim();
      const fallbackCharacterId = characterIdByName.get(String(asset.characterName || '').trim().toLowerCase()) || '';
      const characterId = assetCharacterId || fallbackCharacterId;
      if (!characterId) return;
      counts.set(characterId, (counts.get(characterId) || 0) + 1);
    });
    return counts;
  }, [assets, characters]);
  const selectedCharacterPortraitCount = selectedCharacter
    ? characterPortraitCounts.get(selectedCharacter.id) || 0
    : 0;
  const charactersWithPortraitsCount = characters.filter(
    character => (characterPortraitCounts.get(character.id) || 0) > 0
  ).length;
  const selectedCharacterRefresh = useMemo(
    () => normalizeDescriptionRefreshState(selectedCharacter?.descriptionRefresh),
    [selectedCharacter]
  );
  const selectedCharacterRefreshDisabledReason = useMemo(() => {
    if (!selectedCharacter) return 'Select a character.';
    if (isDescriptionRefreshActive(selectedCharacterRefresh)) {
      return 'Description refresh already running.';
    }
    if (Number(selectedCharacter.chapterCount || 0) <= 0) {
      return 'No linked chapters to analyze.';
    }
    if (Number(selectedCharacterRefresh.remainingCount || 0) <= 0) {
      return 'Free refresh already used.';
    }
    return '';
  }, [selectedCharacter, selectedCharacterRefresh]);
  const hasActiveDescriptionRefresh = useMemo(
    () => characters.some(character => isDescriptionRefreshActive(character.descriptionRefresh)),
    [characters]
  );
  const selectedCharacterAssets = useMemo(
    () =>
      assets.filter(
        asset => {
          const assetCharacterId = String(asset.characterId || '').trim();
          if (assetCharacterId) return assetCharacterId === selectedCharacterId;
          return Boolean(
            selectedCharacter &&
            String(asset.characterName || '').trim().toLowerCase() ===
              String(selectedCharacter.canonicalName || '').trim().toLowerCase()
          );
        }
      ),
    [assets, selectedCharacter, selectedCharacterId]
  );
  const filteredReferenceAssets = useMemo(() => {
    const searchValue = referenceAssetSearch.trim().toLowerCase();
    if (!searchValue) return selectedCharacterAssets;
    return selectedCharacterAssets.filter(asset =>
      [
        asset.characterName,
        asset.prompt,
        asset.stylePreset,
        asset.visualStyle,
        asset.customVisualStyle,
        asset.model,
        asset.provider,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchValue)
    );
  }, [referenceAssetSearch, selectedCharacterAssets]);
  const selectedReferenceAssets = useMemo(
    () => selectedCharacterAssets.filter(asset => selectedReferenceAssetIds.includes(asset.id)),
    [selectedCharacterAssets, selectedReferenceAssetIds]
  );
  const selectedInspirationItems = useMemo(
    () =>
      sortMarketingInspirationItems(
        selectedInspirationIds
          .map(itemId => inspirationItems.find(item => item.id === itemId) || null)
          .filter((item): item is MarketingInspirationItem => Boolean(item))
      ),
    [inspirationItems, selectedInspirationIds]
  );
  const inspirationContext = useMemo(
    () => buildMarketingInspirationContext(selectedInspirationItems),
    [selectedInspirationItems]
  );
  const sizeOptions = useMemo(
    () => mapSizeOptions(sizeOptionsByAspectRatio, aspectRatio),
    [aspectRatio, sizeOptionsByAspectRatio]
  );
  const assetCharacterFilterOptions = useMemo(() => {
    const items = Array.from(
      new Map(
        assets.map(asset => {
          const characterId = String(asset.characterId || '').trim();
          const characterName = String(asset.characterName || '').trim() || 'Unassigned Character';
          const key = characterId || `name:${characterName.toLowerCase()}`;
          return [key, { value: key, label: characterName }];
        })
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
    return [{ value: 'all', label: 'All Characters' }, ...items];
  }, [assets]);
  const assetPresetFilterOptions = useMemo(() => {
    const items = Array.from(
      new Map(
        assets
          .map(asset => {
            const preset = String(asset.stylePreset || '').trim();
            if (!preset) return null;
            return [
              preset,
              {
                value: preset,
                label:
                  STYLE_PRESET_LABELS[preset as MarketingCharacterPresetId] ||
                  preset.replace(/_/g, ' '),
              },
            ] as const;
          })
          .filter(Boolean) as Array<
          readonly [string, { value: string; label: string }]
        >
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
    return [{ value: 'all', label: 'All Presets' }, ...items];
  }, [assets]);
  const filteredAssets = useMemo(() => {
    const searchValue = assetLibrarySearch.trim().toLowerCase();
    return assets.filter(asset => {
      const characterId = String(asset.characterId || '').trim();
      const characterName = String(asset.characterName || '').trim() || 'Unassigned Character';
      const characterKey = characterId || `name:${characterName.toLowerCase()}`;
      if (assetLibraryCharacterFilter !== 'all' && characterKey !== assetLibraryCharacterFilter) {
        return false;
      }
      if (assetLibraryPresetFilter !== 'all' && String(asset.stylePreset || '') !== assetLibraryPresetFilter) {
        return false;
      }
      if (!searchValue) {
        return true;
      }
      const searchableFields = [
        asset.characterName,
        asset.prompt,
        asset.provider,
        asset.model,
        asset.generationId,
        asset.stylePreset,
        asset.visualStyle,
        asset.customVisualStyle,
        asset.aspectRatio,
        asset.size,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchableFields.includes(searchValue);
    });
  }, [assetLibraryCharacterFilter, assetLibraryPresetFilter, assetLibrarySearch, assets]);
  const filteredLibraryGenerations = useMemo(() => {
    const normalizedSearch = assetLibrarySearch.trim().toLowerCase();
    return recentGenerations.filter(generation => {
      const statusValue = String(generation.status || '').trim().toLowerCase();
      if (statusValue === 'completed') {
        return false;
      }
      const characterId = String(generation.characterId || '').trim();
      const characterName = String(generation.characterName || '').trim() || 'Unassigned Character';
      const characterKey = characterId || `name:${characterName.toLowerCase()}`;
      if (assetLibraryCharacterFilter !== 'all' && characterKey !== assetLibraryCharacterFilter) {
        return false;
      }
      if (assetLibraryPresetFilter !== 'all' && String(generation.stylePreset || '') !== assetLibraryPresetFilter) {
        return false;
      }
      if (!normalizedSearch) return true;
      const haystack = [
        generation.characterName,
        generation.prompt,
        generation.stylePreset,
        generation.visualStyle,
        generation.customVisualStyle,
        generation.status,
        generation.error,
        generation.size,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [assetLibraryCharacterFilter, assetLibraryPresetFilter, assetLibrarySearch, recentGenerations]);
  const galleryEntries = useMemo<CharacterGalleryEntry[]>(() => {
    const assetEntries: CharacterGalleryEntry[] = filteredAssets.map(asset => ({
      kind: 'asset',
      key: `asset:${asset.id}`,
      id: asset.id,
      createdAt: asset.createdAt,
      asset,
    }));
    const generationEntries: CharacterGalleryEntry[] = filteredLibraryGenerations.map(generation => ({
      kind: 'generation',
      key: `generation:${generation.id}`,
      id: generation.id,
      createdAt: generation.createdAt,
      characterId: generation.characterId,
      characterName: generation.characterName,
      stylePreset: generation.stylePreset,
      visualStyle: generation.visualStyle,
      customVisualStyle: generation.customVisualStyle,
      size: generation.size,
      status: generation.status,
      prompt: generation.prompt,
      requested: generation.requested || generation.imageCount,
      generated: generation.generated,
      failedCount: generation.failedCount,
      estimatedCredits: generation.estimatedCredits,
      chargedCredits: generation.chargedCredits,
      error: generation.error,
      inspirationSources: generation.inspirationSources,
    }));
    return [...generationEntries, ...assetEntries].sort((left, right) =>
      String(right.createdAt || '').localeCompare(String(left.createdAt || ''))
    );
  }, [filteredAssets, filteredLibraryGenerations]);
  const selectedLibraryAsset = useMemo(
    () => filteredAssets.find(asset => asset.id === selectedLibraryAssetId) || assets.find(asset => asset.id === selectedLibraryAssetId) || null,
    [assets, filteredAssets, selectedLibraryAssetId]
  );
  const pollGeneration = useMemo(
    () =>
      (activeGeneration && isGenerationInFlight(activeGeneration)
        ? activeGeneration
        : recentGenerations.find(item => isGenerationInFlight(item))) || null,
    [activeGeneration, recentGenerations]
  );

  useEffect(() => {
    let cancelled = false;
    setImageModelOptionsLoading(true);
    fetch(`${apiBase}/api/image-models?surface=marketing_characters`)
      .then(response => (response.ok ? response.json() : Promise.reject(new Error('Failed to load image models'))))
      .then((data: ImageModelOption[]) => {
        if (cancelled) return;
        const models = Array.isArray(data) && data.length ? data : DEFAULT_CHARACTER_IMAGE_MODELS;
        setImageModelOptions(models);
        setSelectedImageModel(prev => (
          models.some(model => model.id === prev || model.apiModel === prev)
            ? prev
            : (models[0]?.id || 'gpt-image-2')
        ));
      })
      .catch(() => {
        if (cancelled) return;
        setImageModelOptions(DEFAULT_CHARACTER_IMAGE_MODELS);
        setSelectedImageModel('gpt-image-2');
      })
      .finally(() => {
        if (!cancelled) setImageModelOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  const loadBootstrap = useCallback(async () => {
    if (!userId) return;
    setBootstrapLoading(true);
    try {
      const params = new URLSearchParams({ userId });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load character studio.'));
      }
      const data = (await response.json()) as MarketingCharacterBootstrapResponse;
      const nextCharacters = Array.isArray(data?.characters) ? data.characters : [];
      const nextBook = (data?.book || {}) as MarketingCharacterBookContext;
      const nextSizeOptions =
        data?.sizeOptionsByAspectRatio && Object.keys(data.sizeOptionsByAspectRatio).length
          ? data.sizeOptionsByAspectRatio
          : DEFAULT_SIZE_OPTIONS_BY_ASPECT;
      setBookContext(nextBook);
      setCharacters(nextCharacters);
      setSizeOptionsByAspectRatio(nextSizeOptions);
      setBootstrapError('');
      setUseCoverAsStyleReference(Boolean(nextBook?.hasCoverReference));
      setImageSize(prev => {
        const allowedOptions = nextSizeOptions[aspectRatio] || {};
        if (prev && allowedOptions[prev]) {
          return prev;
        }
        return resolveDefaultSize(nextSizeOptions, aspectRatio);
      });
      setSelectedCharacterId(prev => {
        if (prev && nextCharacters.some((item: MarketingCharacterRecord) => item.id === prev)) {
          return prev;
        }
        return nextCharacters[0]?.id || '';
      });
    } catch (error: any) {
      setBookContext(null);
      setCharacters([]);
      setSizeOptionsByAspectRatio(DEFAULT_SIZE_OPTIONS_BY_ASPECT);
      setBootstrapError(error?.message || 'Failed to load character studio.');
    } finally {
      setBootstrapLoading(false);
    }
  }, [apiBase, aspectRatio, bookId, userId]);

  const loadAssets = useCallback(async () => {
    if (!userId) return;
    setAssetsLoading(true);
    try {
      const params = new URLSearchParams({ userId });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load character assets.'));
      }
      const data = await response.json();
      setAssets(Array.isArray(data?.items) ? data.items : []);
    } catch (error: any) {
      message.error(error?.message || 'Failed to load character assets.');
    } finally {
      setAssetsLoading(false);
    }
  }, [apiBase, bookId, userId]);

  const handleRefreshDescription = useCallback(() => {
    if (!selectedCharacter) return;
    const refreshState = normalizeDescriptionRefreshState(selectedCharacter.descriptionRefresh);
    const chapterCount = Math.max(
      0,
      Number(refreshState.chapterCount || selectedCharacter.chapterCount || 0)
    );
    const hasOverrides = Boolean(selectedCharacter.hasDescriptionOverrides);
    Modal.confirm({
      title: `Refresh ${selectedCharacter.canonicalName}'s description?`,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text>
            This will analyze {chapterCount} chapter{chapterCount === 1 ? '' : 's'} where this
            character appears.
          </Text>
          <Text>This refresh is free, but it can only complete successfully once per character.</Text>
          <Text>Manual description overrides stay in effect and may hide the refreshed inferred summary.</Text>
          {hasOverrides ? (
            <Text type="secondary">This character already has a visible description summary.</Text>
          ) : null}
        </div>
      ),
      okText: 'Refresh description',
      cancelText: 'Cancel',
      onOk: async () => {
        setDescriptionRefreshSubmittingId(selectedCharacter.id);
        try {
          const response = await fetch(
            `${apiBase}/uploadedbooks/book/${encodeURIComponent(bookId)}/characters/${encodeURIComponent(selectedCharacter.id)}/description/refresh`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId }),
            }
          );
          if (!response.ok) {
            throw new Error(
              await getErrorMessage(response, 'Failed to queue character description refresh.')
            );
          }
          const data = await response.json();
          await loadBootstrap();
          message.success(
            data?.message ||
              'Character description refresh queued. Manual overrides remain in effect until cleared.'
          );
        } catch (error: any) {
          message.error(
            error?.message || 'Failed to queue character description refresh.'
          );
        } finally {
          setDescriptionRefreshSubmittingId('');
        }
      },
    });
  }, [apiBase, bookId, loadBootstrap, selectedCharacter, userId]);

  const loadInspiration = useCallback(async () => {
    if (!userId) return;
    setInspirationLoading(true);
    try {
      const params = new URLSearchParams({ userId });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load inspiration items.'));
      }
      const data = await response.json();
      const nextItems = sortMarketingInspirationItems(Array.isArray(data?.items) ? data.items : []);
      setInspirationItems(nextItems);
      setSelectedInspirationIds(prev => prev.filter(itemId => nextItems.some(item => item.id === itemId)));
    } catch (error: any) {
      message.error(error?.message || 'Failed to load inspiration items.');
    } finally {
      setInspirationLoading(false);
    }
  }, [apiBase, bookId, userId]);

  const loadGenerations = useCallback(async () => {
    if (!userId) return;
    try {
      const params = new URLSearchParams({ userId, page: '1', pageSize: '10' });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/generations?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load character generations.'));
      }
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRecentGenerations(items);
      setActiveGeneration(prev => {
        if (prev) {
          const matching = items.find((item: MarketingCharacterGenerationRecord) => item.id === prev.id);
          if (matching) return matching;
        }
        return items[0] || null;
      });
    } catch (error: any) {
      console.error('Failed to load character generations', error);
    }
  }, [apiBase, bookId, userId]);

  const loadGeneration = useCallback(
    async (generationId: string): Promise<MarketingCharacterGenerationRecord | null> => {
      if (!userId || !generationId) return null;
      const params = new URLSearchParams({ userId });
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/generations/${encodeURIComponent(generationId)}?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load generation status.'));
      }
      const data = (await response.json()) as MarketingCharacterGenerationRecord;
      setRecentGenerations(prev => {
        const remaining = prev.filter(item => item.id !== data.id);
        return [data, ...remaining];
      });
      setActiveGeneration(data);
      return data;
    },
    [apiBase, bookId, userId]
  );

  useEffect(() => {
    if (availableViewSet.has('create')) {
      void loadBootstrap();
      void loadInspiration();
    }
    if (availableViewSet.has('create') || availableViewSet.has('library')) {
      void loadGenerations();
      void loadAssets();
    }
  }, [availableViewSet, loadAssets, loadBootstrap, loadGenerations, loadInspiration]);

  useEffect(() => {
    if (inspirationRefreshSignal === undefined) return;
    if (lastInspirationRefreshSignalRef.current === inspirationRefreshSignal) return;
    lastInspirationRefreshSignalRef.current = inspirationRefreshSignal;
    if (availableViewSet.has('create')) void loadInspiration();
  }, [availableViewSet, inspirationRefreshSignal, loadInspiration]);

  useEffect(() => {
    if (!selectedCharacter || (!bookContext && !bookTitle)) return;
    if (promptDirty) return;
    const basePrompt = buildCharacterPrompt(
      bookContext || { title: bookTitle },
      selectedCharacter,
      stylePreset,
      visualStyle,
      customVisualStyle
    );
    const nextPrompt = [basePrompt, inspirationContext].filter(Boolean).join('\n\n');
    setPrompt(nextPrompt);
  }, [bookContext, bookTitle, customVisualStyle, inspirationContext, promptDirty, selectedCharacter, stylePreset, visualStyle]);

  useEffect(() => {
    if (!actionSignal?.ts) return;
    if (actionSignal.action !== 'refresh') return;
    if (availableViewSet.has('create')) {
      void loadBootstrap();
      void loadInspiration();
    }
    if (availableViewSet.has('create') || availableViewSet.has('library')) {
      void loadGenerations();
      void loadAssets();
    }
  }, [actionSignal, availableViewSet, loadAssets, loadBootstrap, loadGenerations, loadInspiration]);

  useEffect(() => {
    if (!availableViewSet.has('create') || !hasActiveDescriptionRefresh) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void loadBootstrap();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [availableViewSet, hasActiveDescriptionRefresh, loadBootstrap]);

  useEffect(() => {
    const nextDefault = resolveDefaultSize(sizeOptionsByAspectRatio, aspectRatio);
    const allowedOptions = sizeOptionsByAspectRatio[aspectRatio] || {};
    if (!imageSize || !allowedOptions[imageSize]) {
      setImageSize(nextDefault);
    }
  }, [aspectRatio, imageSize, sizeOptionsByAspectRatio]);

  useEffect(() => {
    if (studioView === 'library' && availableViewSet.has('library')) {
      void loadAssets();
      void loadGenerations();
    }
  }, [availableViewSet, loadAssets, loadGenerations, studioView]);

  useEffect(() => {
    if (!pollGeneration?.id || !isGenerationInFlight(pollGeneration)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void loadGeneration(pollGeneration.id)
        .then(async generation => {
          if (!generation) return;
          if (!isGenerationInFlight(generation)) {
            await loadAssets();
            await loadGenerations();
            if (fetchCreditBalance) {
              await Promise.resolve(fetchCreditBalance(userId));
            }
          }
        })
        .catch(error => {
          console.error('Failed to poll character generation', error);
        });
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [fetchCreditBalance, loadAssets, loadGeneration, loadGenerations, pollGeneration, userId]);

  useEffect(() => {
    if (!activeGeneration?.id) return;
    const noticeKey = `${activeGeneration.id}:${activeGeneration.status || ''}`;
    if (generationNoticeRef.current === noticeKey) return;
    if (activeGeneration.status === 'completed') {
      generationNoticeRef.current = noticeKey;
      if (activeGeneration.error) {
        message.warning(
          `Character generation completed with ${activeGeneration.generated || 0}/${activeGeneration.requested || activeGeneration.imageCount || 0} image${(activeGeneration.generated || 0) === 1 ? '' : 's'}.`
        );
      } else {
        message.success('Character image generation completed.');
      }
    } else if (activeGeneration.status === 'failed') {
      generationNoticeRef.current = noticeKey;
      message.error(activeGeneration.error || 'Character image generation failed.');
    }
  }, [activeGeneration]);

  useEffect(() => {
    if (!filteredAssets.length) {
      setSelectedLibraryAssetId('');
      return;
    }
    if (!filteredAssets.some(asset => asset.id === selectedLibraryAssetId)) {
      setSelectedLibraryAssetId(filteredAssets[0]?.id || '');
    }
  }, [filteredAssets, selectedLibraryAssetId]);

  useEffect(() => {
    setPreviewAssetLoading(Boolean(previewAsset));
  }, [previewAsset?.id]);

  useEffect(() => {
    if (!selectedCharacterId) {
      setSelectedReferenceAssetIds([]);
      return;
    }
    setSelectedReferenceAssetIds(prev =>
      prev.filter(assetId =>
        assets.some(
          asset =>
            asset.id === assetId && String(asset.characterId || '').trim() === selectedCharacterId
        )
      )
    );
  }, [assets, selectedCharacterId]);

  const resetPrompt = useCallback(() => {
    const basePrompt = buildCharacterPrompt(
      bookContext || { title: bookTitle },
      selectedCharacter,
      stylePreset,
      visualStyle,
      customVisualStyle
    );
    const nextPrompt = [basePrompt, inspirationContext].filter(Boolean).join('\n\n');
    setPrompt(nextPrompt);
    setPromptDirty(false);
  }, [bookContext, bookTitle, customVisualStyle, inspirationContext, selectedCharacter, stylePreset, visualStyle]);

  const toggleReferenceAsset = useCallback((asset: MarketingCharacterAsset) => {
    if (!asset?.id) return;
    if (String(asset.characterId || '').trim() !== selectedCharacterId) {
      message.error('You can only use saved portraits from the selected character.');
      return;
    }
    setSelectedReferenceAssetIds(prev => {
      if (prev.includes(asset.id)) {
        return prev.filter(id => id !== asset.id);
      }
      if (prev.length >= MAX_CHARACTER_REFERENCE_ASSETS) {
        message.warning(`You can select up to ${MAX_CHARACTER_REFERENCE_ASSETS} reference images.`);
        return prev;
      }
      return [...prev, asset.id];
    });
  }, [selectedCharacterId]);

  const handleGenerate = useCallback(async () => {
    const customStyleValue = customVisualStyle.trim();
    if (!selectedCharacterId) {
      message.error('Select a character first.');
      return;
    }
    if (visualStyle === 'custom' && !customStyleValue) {
      message.error('Enter a custom style description before generating.');
      return;
    }
    if (!(prompt || '').trim()) {
      message.error('Enter or reset the prompt before generating.');
      return;
    }
    if (creditBalance && availableCredits < estimatedCredits) {
      message.error('You do not have enough credits to generate these images.');
      return;
    }
    setGenerating(true);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            characterId: selectedCharacterId,
            stylePreset,
            visualStyle,
            customVisualStyle: visualStyle === 'custom' ? customStyleValue : undefined,
            aspectRatio,
            size: imageSize,
            imageCount,
            provider: selectedImageModelConfig.provider,
            model: selectedImageModelConfig.id,
            useCoverAsStyleReference,
            referenceAssetIds: selectedReferenceAssetIds,
            inspirationItemIds: selectedInspirationIds,
            prompt,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to generate character images.'));
      }
      const data = (await response.json()) as MarketingCharacterGenerationRecord;
      setActiveGeneration(data);
      setRecentGenerations(prev => [data, ...prev.filter(item => item.id !== data.id)]);
      setStudioView('create');
      message.success('Character image generation queued.');
    } catch (error: any) {
      message.error(error?.message || 'Failed to generate character images.');
    } finally {
      setGenerating(false);
    }
  }, [
    apiBase,
    aspectRatio,
    availableCredits,
    bookId,
    creditBalance,
    estimatedCredits,
    imageSize,
    imageCount,
    selectedImageModelConfig,
    prompt,
    selectedInspirationIds,
    selectedReferenceAssetIds,
    selectedCharacterId,
    stylePreset,
    useCoverAsStyleReference,
    userId,
    customVisualStyle,
    visualStyle,
  ]);

  const handleDownload = useCallback(
    async (asset: MarketingCharacterAsset) => {
      try {
        const response = await fetch(
          `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets/${encodeURIComponent(asset.id)}/download`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId,
              filename: asset.downloadFilename || `${asset.characterName || 'character'}.png`,
            }),
          }
        );
        if (!response.ok) {
          throw new Error(await getErrorMessage(response, 'Failed to create download link.'));
        }
        const data = await response.json();
        const signedUrl = String(data?.signedUrl || '').trim();
        if (!signedUrl) {
          throw new Error('Missing signed download URL.');
        }
        const link = document.createElement('a');
        link.href = signedUrl;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        message.error(error?.message || 'Failed to download character image.');
      }
    },
    [apiBase, bookId, userId]
  );

  const handleDelete = useCallback(
    async (asset: MarketingCharacterAsset) => {
      try {
        const response = await fetch(
          `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets/${encodeURIComponent(asset.id)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
          }
        );
        if (!response.ok) {
          throw new Error(await getErrorMessage(response, 'Failed to delete character image.'));
        }
        message.success('Character image deleted.');
        setPreviewAsset(null);
        setSelectedLibraryAssetId('');
        setActiveGeneration(prev =>
          prev
            ? {
                ...prev,
                items: (prev.items || []).filter(item => item.id !== asset.id),
                generated: Math.max(0, Number(prev.generated || 0) - 1),
              }
            : prev
        );
        setRecentGenerations(prev =>
          prev.map(generation =>
            generation.items?.some(item => item.id === asset.id)
              ? {
                  ...generation,
                  items: (generation.items || []).filter(item => item.id !== asset.id),
                  generated: Math.max(0, Number(generation.generated || 0) - 1),
                }
              : generation
          )
        );
        await loadAssets();
      } catch (error: any) {
        message.error(error?.message || 'Failed to delete character image.');
      }
    },
    [apiBase, bookId, loadAssets, userId]
  );

  const handleDeleteGeneration = useCallback(
    async (generation: MarketingCharacterGenerationRecord) => {
      try {
        const response = await fetch(
          `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/generations/${encodeURIComponent(generation.id)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
          }
        );
        if (!response.ok) {
          throw new Error(await getErrorMessage(response, 'Failed to remove character generation.'));
        }
        message.success('Failed generation removed.');
        setPreviewGeneration(null);
        setSelectedLibraryGenerationId('');
        setRecentGenerations(prev => prev.filter(item => item.id !== generation.id));
        setActiveGeneration(prev => (prev?.id === generation.id ? null : prev));
        await loadGenerations();
      } catch (error: any) {
        message.error(error?.message || 'Failed to remove character generation.');
      }
    },
    [apiBase, bookId, loadGenerations, userId]
  );

  const createDisabled = !availableViewSet.has('create');
  const libraryDisabled = !availableViewSet.has('library');

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {stableAllowedStudioViews.length > 1 ? (
        <Segmented
          value={studioView}
          options={stableAllowedStudioViews.map(value => ({
            label: value === 'create' ? 'Create' : 'Library',
            value,
          }))}
          onChange={value => setStudioView(value as MarketingCharacterStudioView)}
        />
      ) : null}

      {studioView === 'create' && !createDisabled ? (
        <>
          <Card size="small">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Segmented
                block
                value={authoringMode}
                onChange={value => setAuthoringMode(value as CharacterAuthoringMode)}
                options={[
                  { label: 'Guided', value: 'guided' },
                  { label: 'Customize', value: 'customize' },
                ]}
              />
              <Text type="secondary">
                {authoringMode === 'guided'
                  ? 'Choose the character, use case, and visual style. Recommended technical settings are applied automatically.'
                  : 'Control the model, output dimensions, number of images, references, and exact prompt.'}
              </Text>
              {bootstrapError ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Character Studio is blocked"
                  description={bootstrapError}
                />
              ) : null}

              {!bootstrapError && !bootstrapLoading && characters.length === 0 ? (
                <Empty
                  description="No registry-backed characters are available for this book yet."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : null}

              {!bootstrapError && characters.length > 0 ? (
                <>
                  <Row gutter={[14, 12]} align="top">
                    <Col xs={24} lg={11}>
                      <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <div style={createSectionStyle}>
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong>Creative Setup</Text>
                            <Form layout="vertical" style={{ marginBottom: -16 }}>
                              <Form.Item label={`Character · ${charactersWithPortraitsCount} of ${characters.length} have portraits`}>
                                <Select
                                  value={selectedCharacterId || undefined}
                                  onChange={value => {
                                    setSelectedCharacterId(value);
                                    setPromptDirty(false);
                                  }}
                                  options={characters.map(character => ({
                                    value: character.id,
                                    label: `${character.canonicalName} · ${characterPortraitCounts.get(character.id) || 0
                                      ? `${characterPortraitCounts.get(character.id)} portrait${characterPortraitCounts.get(character.id) === 1 ? '' : 's'}`
                                      : 'No portraits'}`,
                                  }))}
                                  placeholder="Select a character"
                                />
                              </Form.Item>

                              {selectedCharacter ? (
                                <div
                                  style={{
                                    marginBottom: 10,
                                    padding: '8px 10px',
                                    borderRadius: 10,
                                    border: '1px solid var(--border-subtle)',
                                    background: 'rgba(255,255,255,0.02)',
                                  }}
                                >
                                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                    <Space wrap size={[6, 6]}>
                                      <Tag icon={<UserOutlined />}>{selectedCharacter.canonicalName}</Tag>
                                      {selectedCharacter.gender ? <Tag>{selectedCharacter.gender}</Tag> : null}
                                      {selectedCharacter.chapterCount ? <Tag>{selectedCharacter.chapterCount} chapters</Tag> : null}
                                      {selectedCharacter.occurrenceCount ? <Tag>{selectedCharacter.occurrenceCount} mentions</Tag> : null}
                                      <Tag
                                        icon={<PictureOutlined />}
                                        color={selectedCharacterPortraitCount ? 'blue' : 'default'}
                                      >
                                        {assetsLoading
                                          ? 'Checking saved portraits…'
                                          : selectedCharacterPortraitCount
                                            ? `${selectedCharacterPortraitCount} saved portrait${selectedCharacterPortraitCount === 1 ? '' : 's'}`
                                            : 'No saved portraits yet'}
                                      </Tag>
                                    </Space>
                                    <Space wrap size={[8, 8]}>
                                      <Button
                                        size="small"
                                        onClick={handleRefreshDescription}
                                        disabled={
                                          !selectedCharacter ||
                                          Boolean(selectedCharacterRefreshDisabledReason) ||
                                          Boolean(descriptionRefreshSubmittingId)
                                        }
                                        loading={
                                          descriptionRefreshSubmittingId === selectedCharacter.id ||
                                          isDescriptionRefreshActive(selectedCharacterRefresh)
                                        }
                                      >
                                        Refresh Description
                                      </Button>
                                      {getDescriptionRefreshStatusLabel(selectedCharacterRefresh) ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {getDescriptionRefreshStatusLabel(selectedCharacterRefresh)}
                                        </Text>
                                      ) : selectedCharacterRefreshDisabledReason ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {selectedCharacterRefreshDisabledReason}
                                        </Text>
                                      ) : null}
                                    </Space>
                                    {selectedCharacterRefresh.status === 'failed' && selectedCharacterRefresh.lastError ? (
                                      <Text type="danger" style={{ fontSize: 12 }}>
                                        {selectedCharacterRefresh.lastError}
                                      </Text>
                                    ) : null}
                                    {selectedCharacter.descriptionSummary ? (
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {selectedCharacter.descriptionSummary}
                                      </Text>
                                    ) : (
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        No appearance summary is available yet.
                                      </Text>
                                    )}
                                    {selectedCharacter.hasDescriptionOverrides ? (
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        Manual description overrides are active. Refreshed inferred details update underneath them until the overrides are cleared.
                                      </Text>
                                    ) : null}
                                  </Space>
                                </div>
                              ) : null}

                              {authoringMode === 'customize' ? (
                                <Form.Item label="Image Model">
                                  <Select
                                    value={selectedImageModel}
                                    onChange={value => setSelectedImageModel(value)}
                                    loading={imageModelOptionsLoading}
                                    options={imageModelOptions.map(model => ({
                                      value: model.id,
                                      label: model.displayName || model.id,
                                    }))}
                                  />
                                </Form.Item>
                              ) : null}

                              <Form.Item label="Use Case">
                                <Select
                                  value={stylePreset}
                                  onChange={value => {
                                    setStylePreset(value);
                                    setPromptDirty(false);
                                  }}
                                  options={STYLE_PRESET_OPTIONS}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {STYLE_PRESET_HINTS[stylePreset]}
                                </Text>
                              </Form.Item>

                              {authoringMode === 'customize' ? (
                                <Row gutter={12}>
                                  <Col xs={24} md={12}>
                                    <Form.Item label="Aspect Ratio">
                                      <Select
                                        value={aspectRatio}
                                        onChange={value => setAspectRatio(value)}
                                        options={ASPECT_RATIO_OPTIONS}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item label="Generation Size">
                                      <Select
                                        value={imageSize}
                                        onChange={value => setImageSize(value)}
                                        options={sizeOptions}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              ) : null}

                              <Row gutter={12}>
                                {authoringMode === 'customize' ? (
                                  <Col xs={24} md={12}>
                                    <Form.Item label="Image Count">
                                      <InputNumber
                                        min={1}
                                        max={3}
                                        value={imageCount}
                                        onChange={value => setImageCount(Number(value || 1))}
                                        style={{ width: '100%' }}
                                      />
                                    </Form.Item>
                                  </Col>
                                ) : null}
                                <Col xs={24} md={12}>
                                  <Form.Item
                                    label="Use Cover As Style Reference"
                                    valuePropName="checked"
                                    extra={
                                      bookContext?.hasCoverReference
                                        ? 'Keep cover style continuity.'
                                        : 'No cover available yet.'
                                    }
                                  >
                                    <Switch
                                      checked={useCoverAsStyleReference}
                                      disabled={!bookContext?.hasCoverReference}
                                      onChange={checked => setUseCoverAsStyleReference(checked)}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={12}>
                                <Col xs={24} md={12}>
                                  <Form.Item label="Visual Style">
                                    <Select
                                      value={visualStyle}
                                      onChange={value => {
                                        setVisualStyle(value);
                                        setPromptDirty(false);
                                      }}
                                      options={VISUAL_STYLE_OPTIONS.map(option => ({
                                        value: option.value,
                                        label: option.label,
                                      }))}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Form>
                            {visualStyle === 'custom' ? (
                              <Input
                                value={customVisualStyle}
                                onChange={event => {
                                  setCustomVisualStyle(event.target.value);
                                  setPromptDirty(false);
                                }}
                                placeholder="Describe the exact visual style, for example: charcoal illustration with rough paper texture and moody rim lighting"
                              />
                            ) : null}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {visualStyle === 'custom'
                                ? (String(customVisualStyle || '').trim()
                                    ? `Visual style mandate: ${String(customVisualStyle).trim()}. Keep this custom visual style unmistakable in the final image.`
                                    : 'Visual style mandate: follow the user-provided custom visual style direction exactly and keep it consistent across the generated portrait.')
                                : VISUAL_STYLE_OPTIONS.find(option => option.value === visualStyle)?.promptInstruction}
                            </Text>
                          </Space>
                        </div>
                      </Space>
                    </Col>

                    <Col xs={24} lg={13}>
                      <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <div style={createSectionStyle}>
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Text strong>Prompt</Text>
                              <Text type="secondary">
                                Style: {getCharacterVisualStyleLabel(visualStyle, customVisualStyle)}
                                {useCoverAsStyleReference ? ' | Cover reference enabled' : ' | Cover reference disabled'}
                              </Text>
                              {selectedCharacter ? (
                                <Text type="secondary">
                                  Character anchor: {selectedCharacter.canonicalName}
                                </Text>
                              ) : null}
                              {selectedReferenceAssets.length ? (
                                <Text type="secondary">
                                  Reference portraits: {selectedReferenceAssets.length} selected
                                </Text>
                              ) : null}
                            </Space>
                            <TextArea
                              value={prompt}
                              rows={4}
                              onChange={event => {
                                setPrompt(event.target.value);
                                setPromptDirty(true);
                              }}
                              placeholder="Describe the exact character image you want to generate."
                            />
                          </Space>
                        </div>

                        <div style={createSectionStyle}>
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                              <Text strong>Guidance & References</Text>
                              <Space wrap size={6}>
                                {selectedInspirationIds.length ? <Tag color="purple">{selectedInspirationIds.length} inspiration</Tag> : null}
                                {selectedReferenceAssets.length ? <Tag color="blue">{selectedReferenceAssets.length} portrait refs</Tag> : null}
                              </Space>
                            </Space>
                            <Segmented
                              block
                              value={guidanceMode}
                              onChange={value => setGuidanceMode(value as 'inspiration' | 'portraits')}
                              options={[
                                { label: 'Inspiration', value: 'inspiration' },
                                { label: 'Reference Portraits', value: 'portraits' },
                              ]}
                            />
                            <div
                              style={{
                                border: '1px solid var(--border-subtle)',
                                borderRadius: 10,
                                background: 'var(--surface-raised)',
                                padding: 10,
                              }}
                            >
                              {guidanceMode === 'inspiration' ? (
                                <MarketingInspirationPicker
                                  items={inspirationItems}
                                  loading={inspirationLoading}
                                  selectedIds={selectedInspirationIds}
                                  onChange={setSelectedInspirationIds}
                                  selectedCharacterId={selectedCharacterId}
                                  selectedCharacterName={selectedCharacter?.canonicalName}
                                  helperText="Use approved scenes, quotes, and vibes from the Inspiration Library to ground the character prompt before generation."
                                  embedded
                                />
                              ) : (
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <Text strong>Reference Portraits</Text>
                                    <Space size={8}>
                                      <Button size="small" onClick={() => void loadAssets()} loading={assetsLoading}>
                                        Refresh Portraits
                                      </Button>
                                      <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => {
                                          setGuidanceMode('portraits');
                                          setReferencePickerOpen(true);
                                        }}
                                        disabled={!selectedCharacterId}
                                      >
                                        Choose Portraits
                                      </Button>
                                    </Space>
                                  </Space>
                                  <Text type="secondary">
                                    Reuse saved portraits of this character to influence the new generation. Carry style, face, or costume cues across different presets and visual styles. Up to {MAX_CHARACTER_REFERENCE_ASSETS} images.
                                  </Text>
                                  {selectedReferenceAssets.length ? (
                                    <Space wrap size={8}>
                                      {selectedReferenceAssets.map(asset => (
                                        <div
                                          key={`character-reference-${asset.id}`}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            border: '1px solid var(--border-subtle)',
                                            background: 'rgba(255,255,255,0.03)',
                                            borderRadius: 10,
                                            padding: '6px 8px',
                                            maxWidth: '100%',
                                          }}
                                        >
                                          <CharacterPortraitThumbnail
                                            asset={asset}
                                            alt={asset.characterName || 'Character reference'}
                                            style={{
                                              width: 36,
                                              height: 36,
                                              objectFit: 'cover',
                                              borderRadius: 6,
                                              background: 'rgba(255,255,255,0.04)',
                                            }}
                                          />
                                          <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
                                            <Text strong ellipsis style={{ maxWidth: 220, lineHeight: 1.2 }}>
                                              {asset.characterName || 'Character'}
                                            </Text>
                                            <Text type="secondary" ellipsis style={{ maxWidth: 220, fontSize: 12, lineHeight: 1.2 }}>
                                              {STYLE_PRESET_LABELS[asset.stylePreset as MarketingCharacterPresetId] || asset.stylePreset || 'Portrait'}
                                            </Text>
                                          </Space>
                                          <Button size="small" onClick={() => toggleReferenceAsset(asset)}>
                                            Remove
                                          </Button>
                                        </div>
                                      ))}
                                    </Space>
                                  ) : selectedCharacterId ? (
                                    <Text type="secondary">No reference portraits selected.</Text>
                                  ) : (
                                    <Text type="secondary">Select a character first to choose saved portraits.</Text>
                                  )}
                                  {selectedCharacterId && !assetsLoading && selectedCharacterAssets.length === 0 ? (
                                    <Empty
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                      description="Generate a few portraits for this character first, then reuse them as style references."
                                    />
                                  ) : null}
                                </Space>
                              )}
                            </div>
                          </Space>
                        </div>
                      </Space>
                    </Col>
                  </Row>

                  <div style={{ ...createSectionStyle, marginTop: 10 }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <CostSummary
                        creditBalance={creditBalance ? availableCredits : null}
                        baseCost={estimatedCredits}
                        tooltipText="Character Studio charges a flat 1,000 credits per generated image. Size, aspect ratio, cover reference, and prompt edits do not currently change the price."
                        headerActionNode={
                          <Button size="small" onClick={() => setCostBreakdownOpen(true)}>
                            View details
                          </Button>
                        }
                        containerStyle={{ marginBottom: 0 }}
                      />
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12} lg={8}>
                          <Button type="primary" onClick={() => void handleGenerate()} loading={generating} block>
                            Generate Character Images
                          </Button>
                        </Col>
                        <Col xs={24} md={12} lg={8}>
                          <Button onClick={resetPrompt} block>
                            Reset Prompt
                          </Button>
                        </Col>
                        {availableViewSet.has('library') ? (
                          <Col xs={24} lg={8}>
                            <Button onClick={() => setStudioView('library')} block>
                              Open Library
                            </Button>
                          </Col>
                        ) : null}
                      </Row>
                    </Space>
                  </div>
                </>
              ) : null}
            </Space>
          </Card>

        </>
      ) : null}

      {studioView === 'library' && !libraryDisabled ? (
        <Card
          size="small"
          title={libraryTitle}
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                void loadAssets();
                void loadGenerations();
              }}
              loading={assetsLoading}
            >
              Refresh
            </Button>
          }
        >
          {galleryEntries.length === 0 && !assetsLoading ? (
            <Empty
              description="No character images or active generations yet."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div
                className="marketing-character-library-filters"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.6fr) minmax(200px, 1fr) minmax(200px, 1fr) auto',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <Input
                  value={assetLibrarySearch}
                  onChange={event => setAssetLibrarySearch(event.target.value)}
                  placeholder="Search character, prompt, model, preset, or generation"
                  allowClear
                />
                <Select
                  value={assetLibraryCharacterFilter}
                  options={assetCharacterFilterOptions}
                  onChange={value => setAssetLibraryCharacterFilter(value)}
                  style={{ width: '100%' }}
                />
                <Select
                  value={assetLibraryPresetFilter}
                  options={assetPresetFilterOptions}
                  onChange={value => setAssetLibraryPresetFilter(value)}
                  style={{ width: '100%' }}
                />
                <Tag style={{ marginInlineEnd: 0 }}>{galleryEntries.length} shown</Tag>
              </div>

              {galleryEntries.length ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 12,
                  }}
                >
                  {galleryEntries.map(entry => {
                    const isSelected =
                      entry.kind === 'asset'
                        ? entry.id === selectedLibraryAssetId
                        : entry.id === selectedLibraryGenerationId;
                    if (entry.kind === 'generation') {
                      return (
                        <button
                          key={entry.key}
                          type="button"
                          onClick={() => {
                            const generation = recentGenerations.find(item => item.id === entry.id) || null;
                            setSelectedLibraryGenerationId(entry.id);
                            setSelectedLibraryAssetId('');
                            setPreviewAsset(null);
                            setPreviewGeneration(generation);
                          }}
                          style={{
                            width: '100%',
                            padding: 8,
                            borderRadius: 10,
                            border: isSelected ? '1px solid #1677ff' : '1px solid rgba(255,255,255,0.12)',
                            background: isSelected ? 'rgba(22,119,255,0.12)' : '#111827',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              minHeight: 160,
                              maxHeight: 220,
                              overflow: 'hidden',
                              borderRadius: 8,
                              background: '#0f172a',
                              padding: 12,
                              flexDirection: 'column',
                              gap: 8,
                            }}
                          >
                            <Tag color={characterGenerationStatusColor(entry.status)}>
                              {String(entry.status || 'unknown').toUpperCase()}
                            </Tag>
                            <Text type="secondary" style={{ textAlign: 'center' }}>
                              {isGenerationInFlight(entry as MarketingCharacterGenerationRecord)
                                ? 'Character image generation is in progress.'
                                : entry.error || 'Generation did not complete.'}
                            </Text>
                          </div>
                          <Text strong style={{ display: 'block', marginTop: 8 }}>
                            {entry.characterName || 'Character'}
                          </Text>
                          <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                            <Tag color={characterGenerationStatusColor(entry.status)}>
                              {String(entry.status || 'unknown').toUpperCase()}
                            </Tag>
                            {entry.stylePreset ? (
                              <Tag>
                                {STYLE_PRESET_LABELS[entry.stylePreset as MarketingCharacterPresetId] ||
                                  entry.stylePreset}
                              </Tag>
                            ) : null}
                            {entry.size ? <Tag>{entry.size}</Tag> : null}
                            {entry.inspirationSources?.length ? (
                              <Tag>{entry.inspirationSources.length} source{entry.inspirationSources.length === 1 ? '' : 's'}</Tag>
                            ) : null}
                          </Space>
                          {entry.createdAt ? (
                            <Text
                              type="secondary"
                              style={{ display: 'block', marginTop: 8, fontSize: 12, lineHeight: 1.4 }}
                            >
                              Started {formatTimestamp(entry.createdAt)}
                            </Text>
                          ) : null}
                        </button>
                      );
                    }

                    const asset = entry.asset;
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() => {
                          setSelectedLibraryAssetId(asset.id);
                          setSelectedLibraryGenerationId('');
                          setPreviewGeneration(null);
                          setPreviewAsset(asset);
                        }}
                        style={{
                          width: '100%',
                          padding: 8,
                          borderRadius: 10,
                          border: isSelected ? '1px solid #1677ff' : '1px solid rgba(255,255,255,0.12)',
                          background: isSelected ? 'rgba(22,119,255,0.12)' : '#111827',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 160,
                            maxHeight: 220,
                            overflow: 'hidden',
                            borderRadius: 8,
                            background: '#0f172a',
                            padding: 8,
                          }}
                        >
                          <CharacterPortraitThumbnail
                            asset={asset}
                            alt={asset.characterName || 'Character asset'}
                            style={{
                              width: 'auto',
                              height: 'auto',
                              maxWidth: '100%',
                              maxHeight: 200,
                              display: 'block',
                              borderRadius: 6,
                            }}
                          />
                        </div>
                        <Text strong style={{ display: 'block', marginTop: 8 }}>
                          {asset.characterName || 'Character'}
                        </Text>
                        <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                          <Tag color="success">READY</Tag>
                          {asset.stylePreset ? (
                            <Tag>
                              {STYLE_PRESET_LABELS[asset.stylePreset as MarketingCharacterPresetId] ||
                                asset.stylePreset}
                            </Tag>
                          ) : null}
                          {asset.size ? <Tag>{asset.size}</Tag> : null}
                          {asset.inspirationSources?.length ? (
                            <Tag>{asset.inspirationSources.length} source{asset.inspirationSources.length === 1 ? '' : 's'}</Tag>
                          ) : null}
                        </Space>
                        {asset.createdAt ? (
                          <Text
                            type="secondary"
                            style={{ display: 'block', marginTop: 8, fontSize: 12, lineHeight: 1.4 }}
                          >
                            Created {formatTimestamp(asset.createdAt)}
                          </Text>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Empty description="No character images or generations match these filters." />
              )}
            </Space>
          )}
        </Card>
      ) : null}

      <Modal
        title={selectedCharacter ? `Choose ${selectedCharacter.canonicalName} Reference Portraits` : 'Choose Reference Portraits'}
        open={referencePickerOpen}
        onCancel={() => setReferencePickerOpen(false)}
        footer={<Button onClick={() => setReferencePickerOpen(false)}>Done</Button>}
        width={980}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Only saved portraits for the selected character can be used here. Select up to {MAX_CHARACTER_REFERENCE_ASSETS} images.
          </Text>
          <Input
            value={referenceAssetSearch}
            onChange={event => setReferenceAssetSearch(event.target.value)}
            placeholder="Search this character's saved portraits"
            allowClear
          />
          <div
            style={{
              maxHeight: 560,
              overflowY: 'auto',
              border: '1px solid var(--border-subtle)',
              borderRadius: 10,
              padding: 12,
              background: 'var(--surface-muted)',
            }}
          >
            {assetsLoading ? (
              <Text type="secondary">Loading saved portraits...</Text>
            ) : filteredReferenceAssets.length ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 12,
                }}
              >
                {filteredReferenceAssets.map(asset => {
                  const selected = selectedReferenceAssetIds.includes(asset.id);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => toggleReferenceAsset(asset)}
                      style={{
                        width: '100%',
                        borderRadius: 10,
                        border: selected
                          ? '1px solid var(--accent-primary, #1677ff)'
                          : '1px solid var(--border-subtle)',
                        padding: 8,
                        background: selected ? 'rgba(22, 119, 255, 0.12)' : 'var(--surface-raised)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <CharacterPortraitThumbnail
                        asset={asset}
                        alt={asset.characterName || 'Character reference'}
                        style={{
                          width: '100%',
                          aspectRatio: asset.aspectRatio === '1:1' ? '1 / 1' : '2 / 3',
                          objectFit: 'cover',
                          borderRadius: 8,
                          background: 'rgba(255,255,255,0.04)',
                        }}
                      />
                      <Text strong style={{ display: 'block', marginTop: 8 }}>
                        {asset.characterName || 'Character'}
                      </Text>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12, lineHeight: 1.4 }}>
                        {STYLE_PRESET_LABELS[asset.stylePreset as MarketingCharacterPresetId] || asset.stylePreset || 'Portrait'}
                      </Text>
                    </button>
                  );
                })}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  selectedCharacterId
                    ? 'No saved portraits for this character match the current search.'
                    : 'Select a character to choose saved portraits.'
                }
              />
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        open={costBreakdownOpen}
        onCancel={() => setCostBreakdownOpen(false)}
        footer={<Button onClick={() => setCostBreakdownOpen(false)}>Close</Button>}
        title="Character Image Cost Details"
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            Total: <strong>{estimatedCredits.toLocaleString()} credits</strong>
          </Text>
          <Card size="small" styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text>Base rate: {CHARACTER_IMAGE_UNIT_COST.toLocaleString()} credits per image</Text>
              <Text>Requested images: {imageCount}</Text>
              <Text>Formula: {CHARACTER_IMAGE_UNIT_COST.toLocaleString()} x {imageCount} = {estimatedCredits.toLocaleString()} credits</Text>
              <Text type="secondary">Current visual style, preset, size, aspect ratio, cover reference, and prompt edits do not change the current per-image charge.</Text>
            </Space>
          </Card>
          <Text type="secondary">
            Current credits: {creditBalance ? availableCredits.toLocaleString() : 'Loading...'}
          </Text>
        </Space>
      </Modal>

      <Modal
        open={Boolean(previewGeneration)}
        onCancel={() => setPreviewGeneration(null)}
        footer={
          previewGeneration
            ? [
                ...(String(previewGeneration.status || '').trim().toLowerCase() === 'failed'
                  ? [
                      <Popconfirm
                        key="delete-failed"
                        title="Remove this failed generation?"
                        onConfirm={() => void handleDeleteGeneration(previewGeneration)}
                      >
                        <Button danger>Remove Failed Generation</Button>
                      </Popconfirm>,
                    ]
                  : []),
                <Button key="close" onClick={() => setPreviewGeneration(null)}>
                  Close
                </Button>,
              ]
            : null
        }
        width={760}
        title="Character Generation"
        destroyOnClose
      >
        {previewGeneration ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {previewGeneration.error ? (
              <Alert type="error" showIcon message={previewGeneration.error} />
            ) : null}
            <Space wrap>
              <Tag color={characterGenerationStatusColor(previewGeneration.status)}>
                {String(previewGeneration.status || 'unknown').toUpperCase()}
              </Tag>
              {previewGeneration.characterName ? <Tag>{previewGeneration.characterName}</Tag> : null}
              {previewGeneration.stylePreset ? (
                <Tag>
                  {STYLE_PRESET_LABELS[previewGeneration.stylePreset as MarketingCharacterPresetId] ||
                    previewGeneration.stylePreset}
                </Tag>
              ) : null}
              {previewGeneration.size ? <Tag>{previewGeneration.size}</Tag> : null}
            </Space>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary">Started: {formatTimestamp(previewGeneration.createdAt)}</Text>
              {previewGeneration.updatedAt ? (
                <Text type="secondary">Updated: {formatTimestamp(previewGeneration.updatedAt)}</Text>
              ) : null}
              <Text type="secondary">
                Progress: {previewGeneration.generated || 0}/{previewGeneration.requested || previewGeneration.imageCount || 0} generated
              </Text>
              {previewGeneration.failedCount ? (
                <Text type="secondary">Failed attempts: {previewGeneration.failedCount}</Text>
              ) : null}
              {previewGeneration.estimatedCredits ? (
                <Text type="secondary">
                  Estimated credits: {Number(previewGeneration.estimatedCredits || 0).toLocaleString()}
                </Text>
              ) : null}
            </Space>
            {previewGeneration.prompt ? (
              <Card size="small" bodyStyle={{ paddingTop: 12, paddingBottom: 12 }}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>Prompt</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{previewGeneration.prompt}</Paragraph>
                </Space>
              </Card>
            ) : null}
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(previewAsset)}
        onCancel={() => setPreviewAsset(null)}
        footer={
          previewAsset
            ? [
                <Button
                  key="download"
                  onClick={() => void handleDownload(previewAsset)}
                  icon={<DownloadOutlined />}
                >
                  Download
                </Button>,
                <Popconfirm
                  key="delete"
                  title="Delete this character image?"
                  onConfirm={() => void handleDelete(previewAsset)}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                </Popconfirm>,
                <Button key="close" onClick={() => setPreviewAsset(null)}>
                  Close
                </Button>,
              ]
            : null
        }
        width={920}
        title="Character Image"
        destroyOnClose
      >
        {previewAsset ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div
              style={{
                background: '#000',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                minHeight: 320,
                maxHeight: '72vh',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {previewAssetLoading ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--overlay-backdrop)',
                    zIndex: 1,
                    padding: 24,
                  }}
                >
                  <LoadingCard label="Loading image preview..." style={overlayCardStyle} />
                </div>
              ) : null}
              <img
                src={resolveAssetImageSrc(previewAsset)}
                alt={previewAsset.characterName || 'Character image'}
                onLoad={() => setPreviewAssetLoading(false)}
                onError={() => setPreviewAssetLoading(false)}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(72vh - 32px)',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
            <Space size={[6, 6]} wrap>
              {previewAsset.stylePreset ? (
                <Tag>
                  {STYLE_PRESET_LABELS[previewAsset.stylePreset as MarketingCharacterPresetId] ||
                    previewAsset.stylePreset}
                </Tag>
              ) : null}
              {previewAsset.aspectRatio ? <Tag>{previewAsset.aspectRatio}</Tag> : null}
              {previewAsset.size ? <Tag>{previewAsset.size}</Tag> : null}
            </Space>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary">Created: {formatTimestamp(previewAsset.createdAt)}</Text>
              {previewAsset.provider ? (
                <Text type="secondary">Provider: {previewAsset.provider}</Text>
              ) : null}
              {previewAsset.model ? <Text type="secondary">Model: {previewAsset.model}</Text> : null}
            </Space>
            {previewAsset.prompt ? (
              <Card size="small" bodyStyle={{ paddingTop: 12, paddingBottom: 12 }}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>Prompt</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{previewAsset.prompt}</Paragraph>
                </Space>
              </Card>
            ) : null}
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
};

export default MarketingCharacterModule;
